import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  CircularProgress,
  Alert,
  Grid,
  Paper
} from '@mui/material';
import { 
  Payment, 
  Launch, 
  Info,
  AccountBalance,
  Security
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useReactiveVar } from '@apollo/client';
import { userDeatils } from '../../ReactiveVariables/index';
import { GET_ONE_CATERER } from '../../Graphql/queries';
import { CREATE_STRIPE_CONNECT_ACCOUNT, GENERATE_STRIPE_ONBOARDING_URL, REFRESH_STRIPE_ACCOUNT_STATUS } from '../../Graphql/mutations';
import { toggleSnackbar } from '../../ReactiveVariables/index';
import LoggedHeader from '../Common/loggedHeader';
import SideMenu from '../../components/organisms/sidebar';
import {
  canReceiveStripeTransfers,
  getStripeTransferBlockedMessage,
} from './stripeTransferStatus';

export default function StripeSetupPage() {
  const user = useReactiveVar(userDeatils);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [stripeAccountData, setStripeAccountData] = useState(null);

  const catererId = user?.data?.login?.catererId || user?.data?.currentUserDetails?.catererId || user?.data?.createCaterer?.catererId;

  const { data: catererData, loading, refetch } = useQuery(GET_ONE_CATERER, {
    variables: { getOneCatererId: catererId, agsType: "id" },
    skip: !catererId,
    fetchPolicy: "network-only"
  });

  // Clear stripeAccountData when query data updates
  useEffect(() => {
    if (catererData?.getOneCaterer?.stripeAccountId && stripeAccountData) {
      setStripeAccountData(null);
    }
  }, [catererData?.getOneCaterer?.stripeAccountId]);

  const [createStripeConnectAccount] = useMutation(CREATE_STRIPE_CONNECT_ACCOUNT, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: async (data) => {
      if (data.createStripeConnectAccount.error) {
        toggleSnackbar({
          status: true,
          message: data.createStripeConnectAccount.error,
          variant: "error",
        });
      } else {
        setStripeAccountData(data.createStripeConnectAccount);
        toggleSnackbar({
          status: true,
          message: "Stripe account created successfully!",
          variant: "success",
        });
        // Manually refetch to ensure UI updates
        await refetch();
      }
      setIsCreatingAccount(false);
    },
    onError: (error) => {
      console.error("Error creating Stripe account:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to create Stripe account",
        variant: "error",
      });
      setIsCreatingAccount(false);
    },
  });

  const [generateStripeOnboardingUrl] = useMutation(GENERATE_STRIPE_ONBOARDING_URL, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      if (data.generateStripeOnboardingUrl.error) {
        toggleSnackbar({
          status: true,
          message: data.generateStripeOnboardingUrl.error,
          variant: "error",
        });
      } else {
        setStripeAccountData(data.generateStripeOnboardingUrl);
        toggleSnackbar({
          status: true,
          message: "Stripe onboarding URL generated successfully!",
          variant: "success",
        });
      }
      setIsGeneratingUrl(false);
    },
    onError: (error) => {
      console.error("Error generating Stripe onboarding URL:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to generate Stripe onboarding URL",
        variant: "error",
      });
      setIsGeneratingUrl(false);
    },
  });

  const [refreshStripeAccountStatus] = useMutation(REFRESH_STRIPE_ACCOUNT_STATUS, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      if (data.refreshStripeAccountStatus.error) {
        toggleSnackbar({
          status: true,
          message: data.refreshStripeAccountStatus.error,
          variant: "error",
        });
      } else {
        console.log("✅ Stripe account status refreshed:", data.refreshStripeAccountStatus);
        toggleSnackbar({
          status: true,
          message: "Stripe account status updated!",
          variant: "success",
        });
      }
    },
    onError: (error) => {
      console.error("Error refreshing Stripe account status:", error);
      toggleSnackbar({
        status: true,
        message: error.message || "Failed to refresh Stripe account status",
        variant: "error",
      });
    },
  });

  const handleCreateAccount = () => {
    setIsCreatingAccount(true);
    createStripeConnectAccount({
      variables: { catererId }
    });
  };

  const handleCompleteOnboarding = async () => {
    if (isGeneratingUrl) return;
    
    setIsGeneratingUrl(true);
    
    try {
      console.log("🔍 Checking Stripe account status before generating URL...");
      
      // First, refresh the account status to get the latest from Stripe
      await refreshStripeAccountStatus({
        variables: { catererId }
      });
      
      console.log("🔍 Generating Stripe onboarding URL for catererId:", catererId);
      
      const result = await generateStripeOnboardingUrl({
        variables: { catererId }
      });
      
      console.log("🔍 Result from generateStripeOnboardingUrl:", result);
      
      if (result.data?.generateStripeOnboardingUrl?.onboardingUrl) {
        const url = result.data.generateStripeOnboardingUrl.onboardingUrl;
        console.log("🔍 Opening URL in new tab:", url);
        
        // Open in new tab instead of popup (better for Safari and mobile)
        window.open(url, '_blank');
      } else {
        console.log("❌ No onboarding URL in result:", result);
        toggleSnackbar({
          status: true,
          message: "Failed to generate onboarding URL. Please try again.",
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error generating onboarding URL:", error);
      toggleSnackbar({
        status: true,
        message: "Failed to generate onboarding URL. Please try again.",
        variant: "error",
      });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  // Use mutation response data if available, otherwise use query data
  const stripeAccountId = stripeAccountData?.stripeAccountId || catererData?.getOneCaterer?.stripeAccountId;
  const stripeDetails = catererData?.getOneCaterer || {};
  const transfersEnabled = canReceiveStripeTransfers(stripeDetails);
  const blockedMessage = getStripeTransferBlockedMessage(stripeDetails);

  if (loading) {
    return (
      <div>
        <LoggedHeader title="Stripe Setup" />
        <SideMenu selected="Stripe Setup" user="caterer" />
        <div className="contentWrap">
          <div className="contentWrapInner">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <LoggedHeader title="Stripe Setup" />
      <SideMenu selected="Stripe Setup" user="caterer" />
      <div className="contentWrap">
        <div className="contentWrapInner">
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4">
              Payment Processing Setup
            </Typography>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {/* Status Overview */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Payment sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">
                  Stripe Connect Status
                </Typography>
              </Box>
              
              {transfersEnabled ? (
                <Alert severity="success">
                  <Typography variant="h6">Transfers Enabled</Typography>
                  <Typography variant="body2">
                    This Stripe Connect account can receive transfers from Caterplace.
                  </Typography>
                </Alert>
              ) : stripeAccountId ? (
                <Alert severity="warning">
                  <Typography variant="h6">Transfers Pending</Typography>
                  <Typography variant="body2">
                    {blockedMessage}
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="h6">Stripe Setup Required</Typography>
                  <Typography variant="body2">
                    Set up your Stripe Connect account to start accepting payments and receiving transfers.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Setup Steps */}
          <Grid container spacing={3}>
            {/* Step 1: Create Account */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <AccountBalance sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6">Step 1: Create Stripe Account</Typography>
                  </Box>
                  
                  {!stripeAccountId ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Create your Stripe Connect account to start accepting payments.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateAccount}
                        disabled={isCreatingAccount}
                        startIcon={isCreatingAccount ? <CircularProgress size={16} /> : <Payment />}
                        fullWidth
                      >
                        {isCreatingAccount ? 'Creating Account...' : 'Create Stripe Account'}
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        ✅ Stripe account created successfully
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Account ID: {stripeAccountId}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Step 2: Complete Onboarding */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Security sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6">Step 2: Enable Transfers</Typography>
                  </Box>
                  
                  {stripeAccountId && !transfersEnabled ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {blockedMessage}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCompleteOnboarding}
                        disabled={isGeneratingUrl}
                        startIcon={isGeneratingUrl ? <CircularProgress size={16} /> : <Launch />}
                        fullWidth
                      >
                        {isGeneratingUrl ? 'Generating...' : 'Review Stripe Requirements'}
                      </Button>
                    </Box>
                  ) : transfersEnabled ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Transfers are enabled for this Stripe account.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Stripe transfer capability: Active
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Complete Step 1 first to create your Stripe account.
                      </Typography>
                      <Button
                        variant="outlined"
                        disabled
                        fullWidth
                      >
                        Complete Verification
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Help Text */}
          <Paper sx={{ mt: 3, p: 2, backgroundColor: 'grey.50' }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Need help?</strong> The Stripe onboarding process typically takes 5-10 minutes to complete. 
              You'll need your business information, bank account details, and may need to provide additional documentation 
              depending on your business type.
            </Typography>
          </Paper>
        </div>
      </div>
    </div>
  );
}
